import { useCallback, useEffect, useMemo, useState } from "react";
import { IntlShape, useIntl } from "react-intl";
import { useHistory } from "react-router";

import { ImpactCategory } from "../../../__generated__/globalTypes";
import useUserInfo from "../../../data-store/useUserInfo";
import {
  ImpactRating,
  impactRatingToLongName,
} from "../../../domain/impactRatings";
import { highD, lowB, mediumC, veryHighE, veryLowA } from "../../graphs/colors";
import ScatterChart from "../../graphs/ScatterChart";
import Label from "../../labels/Label";
import { useImpactCategory } from "../ImpactCategoryProvider";

import "./SalesAndAverageImpactByTags.css";

const ratingColorMap = {
  VLOW: veryLowA,
  LOW: lowB,
  MEDIUM: mediumC,
  HIGH: highD,
  VHIGH: veryHighE,
};

function generateTagSVG(fillColor: string) {
  return `<svg width="51" height="51" viewBox="0 0 51 51" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="51" height="51" fill="transparent" />
    <g transform="translate(-6, 12)" filter="url(#filter0_d_22904_4516)">
      <path d="M20.8984 10.202C21.3019 9.79866 21.8584 9.58696 22.4279 9.6199L29.4859 10.028L29.6806 10.0487C30.5759 10.1882 31.2682 10.9172 31.3607 11.8186L31.371 12.014L31.4056 18.6555C31.4082 19.1893 31.1974 19.7019 30.82 20.0794L21.6573 29.2421C20.8763 30.0232 19.6099 30.0232 18.8289 29.2421L11.7578 22.171C10.9768 21.39 10.9768 20.1237 11.7578 19.3426L20.8984 10.202Z" fill="${fillColor}" stroke="white" stroke-width="2" stroke-linejoin="round"/>
    </g>
    <defs>
      <filter id="filter0_d_22904_4516" x="-1.14062" y="-4.1416" width="48.2852" height="48.2842" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="2"/>
        <feGaussianBlur stdDeviation="5"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.14 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_22904_4516"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_22904_4516" result="shape"/>
      </filter>
    </defs>
  </svg>`;
}

const AVERAGE_DATA_MAP = {
  GHG: "average_ghg_intensity",
  LAND_USE: "average_land_use_intensity",
  WATER_USE: "average_water_use_intensity",
};

export function useSVGAsImage(svgString: string, width = 40, height = 40) {
  const [image, setImage] = useState<HTMLImageElement | null>(null);

  useEffect(() => {
    const svg = encodeURIComponent(svgString.trim());
    const imageUrl = `data:image/svg+xml,${svg}`;
    const img = new Image(width, height);
    img.src = imageUrl;
    img.onload = () => setImage(img);
  }, [svgString, width, height]);

  return image;
}

const SalesAndAverageImpactByTags = ({
  impactRatingInfo,
  tags,
}: {
  impactRatingInfo: any;
  tags: any[];
}) => {
  const { impactCategory } = useImpactCategory();
  const [{ locale }] = useUserInfo();
  const intl = useIntl();
  const history = useHistory();

  const [icons, setIcons] = useState<
    Record<string, { small: HTMLImageElement; big: HTMLImageElement }>
  >({});
  const [thresholds, setThresholds] = useState<any[]>([]);

  useEffect(() => {
    const colorMap = ratingColorMap;

    const newThresholds = impactRatingInfo.impactRatingInfo.map(
      (entry: any, index: number, arr: any[]) => {
        const color = (colorMap as any)[entry.impactRating as any];
        const next = arr[index + 1];
        const upperBound =
          next?.lowerBound ??
          Math.round(
            Math.max(
              tags.sort(
                (a, b) =>
                  b[AVERAGE_DATA_MAP[impactCategory]] -
                  a[AVERAGE_DATA_MAP[impactCategory]]
              )[0][AVERAGE_DATA_MAP[impactCategory]],
              entry.lowerBound
            ) * 1.2
          );

        return {
          label: entry.label,
          rating: entry.impactRating,
          lowerBound: entry.lowerBound ?? 0,
          upperBound,
          color,
        };
      }
    );

    setThresholds(newThresholds);

    // Generate SVG icons per color
    newThresholds.forEach((t: any) => {
      const svg = generateTagSVG(t.color);
      const smallImg = new Image(40, 40);
      smallImg.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
      const bigImg = new Image(50, 50);
      bigImg.src = `data:image/svg+xml,${encodeURIComponent(svg)}`;
      smallImg.onload = () => {
        setIcons((prev) => ({
          ...prev,
          [t.rating]: { small: smallImg, big: bigImg },
        }));
      };
    });
  }, [impactCategory, impactRatingInfo, tags]);

  const data = useMemo(() => {
    if (!thresholds.length || !icons || !Object.keys(icons).length) return null;
    return {
      datasets: [
        {
          label: "Products",
          data: tags.map((tag) => ({
            y: tag[AVERAGE_DATA_MAP[impactCategory]],
            x: tag.total_sales,
            id: tag.id,
            impact: thresholds.find(
              (t) =>
                tag[AVERAGE_DATA_MAP[impactCategory]] >= t.lowerBound &&
                tag[AVERAGE_DATA_MAP[impactCategory]] < t.upperBound
            )?.rating,
            productCount: tag.product_count,
            name: tag.name,
          })),
          pointBackgroundColor: (ctx: any) => {
            if (ctx.raw == null) return "";
            const y = ctx.raw.y;
            const threshold = thresholds.find(
              (t) => y >= t.lowerBound && y < t.upperBound
            );
            return threshold?.color ?? "#000";
          },
          pointStyle: (ctx: any) => {
            if (ctx.raw == null) return "";
            const y = ctx.raw.y;
            const threshold = thresholds.find(
              (t) => y >= t.lowerBound && y < t.upperBound
            );
            const rating = threshold?.rating;
            return ctx.active ? icons[rating]?.big : icons[rating]?.small;
          },
          pointRadius: 10,
          hoverRadius: 10,
          hitRadius: 10,
        },
      ],
    };
  }, [icons, impactCategory, tags, thresholds]);

  const customTooltip = useCallback(
    (tooltip: any) => {
      const datapoint =
        tooltip.dataPoints[0].dataset.data[tooltip.dataPoints[0].dataIndex];
      return (
        <Tooltip
          impactCategory={impactCategory}
          rating={datapoint.impact}
          name={datapoint.name}
          productCount={datapoint.productCount}
          sales={datapoint.x}
          impact={datapoint.y}
          locale={locale}
          intl={intl}
        />
      );
    },
    [impactCategory, locale, intl]
  );

  const yAxisTitle = useMemo(() => {
    switch (impactCategory) {
      case "GHG":
        return intl.formatMessage({
          id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/yAxisTitle:ghg",
          defaultMessage: "GHG Intensity of products (kg CO2e / kg)",
        });
      case "WATER_USE":
        return intl.formatMessage({
          id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/yAxisTitle:water",
          defaultMessage: "Water Use Intensity of products (L / kg)",
        });
      case "LAND_USE":
        return intl.formatMessage({
          id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/yAxisTitle:land",
          defaultMessage: "Land Use Intensity of products (m²year / kg)",
        });
    }
  }, [impactCategory, intl]);

  const handleClick = useCallback(
    (datapoint: any) => {
      if (!datapoint) return;
      history.push(
        `sales-data?filterToSelectedCollectionIds=${datapoint.id}&category=${impactCategory}`
      );
    },
    [history, impactCategory]
  );

  const TAG_CHART_TITLE = useMemo(() => {
    if (impactCategory === "GHG") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/title:GHG",
        defaultMessage: "Sales and Average GHG Intensity by Tag",
      });
    }
    if (impactCategory === "LAND_USE") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/title:LandUse",
        defaultMessage: "Sales and Average Land Use Intensity by Tag",
      });
    }
    if (impactCategory === "WATER_USE") {
      return intl.formatMessage({
        id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/title:WaterUse",
        defaultMessage: "Sales and Average Water Use Intensity by Tag",
      });
    }
  }, [impactCategory, intl]);

  if (!thresholds.length || !data) return null;
  return (
    <div style={{ height: "100%" }}>
      <div style={{ height: "100%" }}>
        <h4>{TAG_CHART_TITLE}</h4>
        <ScatterChart
          xAxisTtile={intl.formatMessage({
            id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/xAxisTitle",
            defaultMessage: "Sales (products sold)",
          })}
          yAxisTtile={yAxisTitle}
          data={data}
          thresholds={thresholds}
          customTooltip={customTooltip}
          handleClick={handleClick}
        />
      </div>
    </div>
  );
};

interface TooltipProps {
  impactCategory: ImpactCategory;
  intl: IntlShape;
  rating: ImpactRating;
  locale: string | null;
  name: string;
  productCount: number;
  sales: number;
  impact: number;
}

function Tooltip(props: TooltipProps) {
  const impactTitle = useMemo(() => {
    switch (props.impactCategory) {
      case "GHG":
        return props.intl.formatMessage({
          defaultMessage: "GHG Intensity",
          id: "domain/EffectType:ghgPerKg/title",
        });
      case "WATER_USE":
        return props.intl.formatMessage({
          defaultMessage: "Water Use",
          id: "domain/EffectType:waterUse/title",
        });
      case "LAND_USE":
        return props.intl.formatMessage({
          defaultMessage: "Land Use",
          id: "domain/EffectType:landUse/title",
        });
    }
  }, [props.impactCategory, props.intl]);

  const unit = useMemo(() => {
    switch (props.impactCategory) {
      case "GHG":
        return props.intl.formatMessage({
          id: "util/units:kgco2eperkg",
          defaultMessage: `kg CO₂e / kg`,
        });
      case "LAND_USE":
        return props.intl.formatMessage({
          defaultMessage: "m²year / kg",
          id: "util/units:m2yearperkg",
        });
      case "WATER_USE":
        return props.intl.formatMessage({
          defaultMessage: "L / kg",
          id: "util/units:Lperkg",
        });
    }
  }, [props.impactCategory, props.intl]);

  return (
    <div
      className="EmissionSalesChart_Tooltip"
      style={{ width: "fit-content" }}
    >
      <div className="d-flex" style={{ flexDirection: "column", gap: 4 }}>
        <div className="SalesAndAverageImpactByTags_tooltip_medium_value">
          {props.name}
        </div>
        <div className="d-flex" style={{ flexDirection: "row", gap: 16 }}>
          <div className="d-flex" style={{ flexDirection: "column", gap: 6 }}>
            <div
              className="d-flex"
              style={{ flexDirection: "column", flexGrow: 1 }}
            >
              <div className="SalesAndAverageImpactByTags_tooltip_medium_value">
                {impactRatingToLongName(props.rating, props.intl)}
              </div>
              <div className="SalesAndAverageImpactByTags_tooltip_small_description">
                {impactTitle}
              </div>
            </div>
            <div className="d-flex" style={{ flexDirection: "row", gap: 16 }}>
              <div className="d-flex" style={{ flexDirection: "column" }}>
                <div className="SalesAndAverageImpactByTags_tooltip_medium_value">
                  {props.intl.formatNumber(props.sales)}
                </div>
                <div className="SalesAndAverageImpactByTags_tooltip_small_description">
                  {props.intl.formatMessage({
                    id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/tooltip/sales",
                    defaultMessage: "sales",
                  })}
                </div>
              </div>
              <div className="d-flex" style={{ flexDirection: "column" }}>
                <div className="SalesAndAverageImpactByTags_tooltip_medium_value">
                  {props.productCount}
                </div>
                <div className="SalesAndAverageImpactByTags_tooltip_small_description">
                  {props.intl.formatMessage({
                    id: "components/pages:SalesDashBoard/SalesAndAverageImpactByTags/tooltip/products",
                    defaultMessage: "products",
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex" style={{ flexDirection: "column", gap: 6 }}>
            <div style={{ height: 36 }}>
              <Label
                colourSetting="colour"
                type="letterRating"
                impactCategory={props.impactCategory}
                impactRating={props.rating}
                width={46}
                locale={props.locale}
              />
            </div>
            <div className="d-flex" style={{ flexDirection: "column" }}>
              <div className="SalesAndAverageImpactByTags_tooltip_medium_value">
                {props.impact.toFixed(2)}
              </div>
              <div className="SalesAndAverageImpactByTags_tooltip_small_description">
                {unit}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SalesAndAverageImpactByTags;
